import { partyHorn } from "@simplybusiness/icons";
import { Icon, RadioGroup, Segment, Text } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { PaymentMethodRadio } from "./PaymentMethodRadio";
import { PromptContent } from "./PromptContent";
import type { PaymentMethodRadioData, PaymentOptionsProps } from "./types";

const hasToPayToday = (
  option: PaymentMethodRadioData,
): option is PaymentMethodRadioData & { toPayToday: string } =>
  option.toPayToday != null;

export const PaymentOptions = ({
  options,
  name,
  selectedOption,
  isCombined = false,
  isLoading = false,
  isError = false,
  errorMessage,
  markedForPurchase = true,
  savingsMessage,
  onChange,
  prompt,
  onPromptAction,
  isPromptActionLoading,
  onRemoveProduct,
  isRemoveLoading,
}: PaymentOptionsProps) => {
  if (prompt) {
    return (
      <PromptContent
        prompt={prompt}
        onPromptAction={onPromptAction}
        isPromptActionLoading={isPromptActionLoading}
        onRemoveProduct={onRemoveProduct}
        isRemoveLoading={isRemoveLoading}
      />
    );
  }

  return (
    <Segment
      colour="subtle"
      className={classNames("mobius-payment-options", {
        "mobius-payment-options--excluded": !markedForPurchase,
        "mobius-payment-options--combined": isCombined,
      })}
      data-testid="payment-options"
    >
      <Text elementType="p" data-testid="payment-options-title">
        Payment Options
      </Text>

      <RadioGroup
        name={name}
        data-testid="radio-group"
        value={selectedOption}
        aria-label="Payment options"
        onChange={e => onChange?.(e.target.value)}
        isDisabled={isLoading}
        isInvalid={isError}
        errorMessage={isError ? errorMessage : undefined}
      >
        {options.filter(hasToPayToday).map(option => (
          <PaymentMethodRadio
            key={option.value}
            {...option}
            isCombined={isCombined}
          />
        ))}
      </RadioGroup>

      {savingsMessage && (
        <Text variant="small" className="mobius-payment-options__savings">
          <Icon icon={partyHorn} aria-hidden="true" />
          &nbsp;{savingsMessage}
        </Text>
      )}
    </Segment>
  );
};

PaymentOptions.displayName = "PaymentOptions";
